import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getCurrentUser, trackAnalyticsEvent } from '@/lib/api';

// Generate or retrieve session ID
const getSessionId = (): string => {
  let sessionId = sessionStorage.getItem('analytics_session_id');
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('analytics_session_id', sessionId);
  }
  return sessionId;
};

// Detect device type
const getDeviceType = (): string => {
  const ua = navigator.userAgent;
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
    return 'tablet';
  }
  if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
    return 'mobile';
  }
  return 'desktop';
};

// Detect product ID from current route
const getProductIdFromRoute = (pathname: string, userPrimaryProduct?: string | null): string | null => {
  // Admin routes - track as 'admin'
  if (pathname.startsWith('/admin')) {
    return 'admin';
  }

  // Product-specific routes
  if (pathname.includes('/products/mindcotine') || pathname.includes('/mindcotine')) {
    return 'mindcotine';
  }
  // Check for mindco-relief paths
  if (pathname.includes('/products/mindco-relief') || pathname.includes('/mindco-relief')) {
    return 'mindco-relief';
  }

  // Shared routes - use user's primary product (which will be 'mindcotine' or 'mindco-relief')
  return userPrimaryProduct || null;
};

interface AnalyticsProviderProps {
  children: React.ReactNode;
}

export function AnalyticsProvider({ children }: AnalyticsProviderProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const previousPathRef = useRef<string>('');

  // Track all page views globally
  useEffect(() => {
    const currentPath = location.pathname + location.search;

    // Skip if same page
    if (previousPathRef.current === currentPath) {
      return;
    }

    previousPathRef.current = currentPath;

    const trackPageView = async () => {
      try {
        // Add timeout to prevent analytics from blocking page loads
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Analytics timeout')), 5000)
        );

        const trackingPromise = (async () => {
          const userResult = await getCurrentUser();
          const userId = userResult.success ? userResult.data.id : null;
          // primary_product_id is on profiles table, not auth.users - rely on route-based detection
          const userPrimaryProduct = null;

          // Detect product from current route and user preference
          const product_id = getProductIdFromRoute(location.pathname, userPrimaryProduct);

          await trackAnalyticsEvent(userId, getSessionId(), {
            event_type: 'page_view',
            event_name: t('common.analyticsprovider.page_view', 'Page View'),
            event_data: {
              page_path: location.pathname,
              path: location.pathname,
              search: location.search,
              hash: location.hash,
            },
            page_path: location.pathname,
            referrer: document.referrer,
            user_agent: navigator.userAgent,
            device_type: getDeviceType(),
            product_id: product_id || undefined, // Include product context for page views
          });
        })();

        // Race between tracking and timeout - whichever finishes first
        await Promise.race([trackingPromise, timeoutPromise]);
      } catch (error) {
        // Silently fail - analytics should never block page loads
        console.debug('Analytics page view tracking error:', error);
      }
    };

    // Fire and forget - don't await to ensure non-blocking
    trackPageView();
  }, [location, t]);

  return <>{children}</>;
}
